import type { assetCategory, FileExtension } from '@/enums';

export interface Identifiable {
    id: number;
}

/** GET /oembed/resolve response data (live on test8 since 2026-09-03). */
export interface OembedResolveData {
    provider: string;
    url: string;
    title: string | null;
    author_name: string | null;
    thumbnail_url: string | null;
    /** Untrusted, informational — the client builds its own embeds. */
    html: string | null;
    /** Seconds; only Vimeo populates it among the allowlisted providers. */
    duration: number | null;
}

/** oEmbed payload subset the backend stores for a video-link asset (GET /oembed/resolve shape). */
export interface VideoLinkMeta {
    provider: string;
    title: string | null;
    /** Remote provider URL — deliberately not hosted by us (SSRF/storage trade-off, 2026-09-01). */
    thumbnail_url: string | null;
    /** Seconds; only Vimeo populates it among the allowlisted providers. */
    duration: number | null;
}

export interface Assets {
    id: number;
    name: string;
    category: assetCategory;
    path: string;
    main: number;
    order?: number;
    mime?: string;
    /**
     * Video-link assets (oEmbed, #1832): a non-null source_url IS the marker
     * that this asset is a pasted video link rather than an uploaded file —
     * `category` keeps meaning the collection. Backend-pending; no client
     * mock exists (provider oEmbed has no CORS).
     */
    source_url?: string | null;
    meta?: VideoLinkMeta | null;
}
export interface ResponseType<T> {
    success: boolean;
    data: T;
    message: string | null;
}

export interface IColor {
    color: string;
    code: string;
}

export type MatrixRow<ItemType> = { [key: number]: ItemType[] };
export type UpdateResultAbstract<ItemType> =
    | undefined
    | { data: ResponseType<ItemType> }
    | { error: { status: number | string; data?: unknown } };
export type UpdateResult<UpdateOperationReturn, ItemType> = UpdateResultAbstract<ItemType> | UpdateOperationReturn;
export type UpdateResultPromise<UpdateOperationReturn, ItemType> = Promise<
    undefined | { data: ResponseType<ItemType> } | { error: { status: number | string; data?: unknown } } | UpdateOperationReturn
>;
export type ValidValue<T> = Exclude<T, null | undefined | 0 | '' | false>;

export type SearchProps = {
    searchText: string;
    str: string;
    indexStep?: number;
    scrollStep?: number;
};

export type TExport = 'synopsis' | 'episodes-synopsis' | 'pep-scene' | 'outline';
export interface IFileExport {
    project_id: number;
    type?: TExport;
    tags?: string;
    format?: FileExtension;
    episode_id?: number | null;
    /** Outline export: `false` → full script, `true` → synopsis only. Omitted for other export types. */
    outlineOnly?: boolean;
}

// ============= API Error =============

export interface ApiError {
    data: {
        message: string;
        error_codes?: string[];
    };
}

export const isApiError = (error: unknown): error is ApiError =>
    typeof error === 'object' &&
    error !== null &&
    'data' in error &&
    typeof (error as ApiError).data === 'object' &&
    (error as ApiError).data !== null &&
    'message' in (error as ApiError).data;
